<template>
    <article class="grid grid-cols-[56px_minmax(0,1fr)] gap-x-3.5 gap-y-4 rounded-2xl border border-brand-blue-gray bg-white p-5 sm:grid-cols-[180px_minmax(0,1fr)] sm:gap-x-6 sm:p-6">
        <div class="self-center sm:self-start">
            <img v-if="showsAvatar"
                 class="h-14 w-14 rounded-xl object-cover sm:aspect-square sm:h-auto sm:w-full"
                 :src="attorney.avatarUrl"
                 :alt="attorney.name"
                 referrerpolicy="no-referrer"
                 loading="lazy"
                 width="180"
                 height="180"
                 @error="avatarFailed = true">

            <div v-else class="flex h-14 w-14 items-center justify-center rounded-xl bg-brand-teal font-access-legal text-2xl text-white sm:aspect-square sm:h-auto sm:w-full sm:text-6xl" aria-hidden="true">{{ attorney.initial }}</div>
        </div>

        <div class="contents sm:flex sm:flex-col sm:gap-5">
            <h3 class="self-center break-words font-access-legal text-[23px]/[1.1] tracking-tight text-brand-teal sm:self-start sm:text-3xl/[1.1]">{{ attorney.name }}</h3>

            <div v-if="attorney.practiceAreasLine" class="col-span-2">
                <p class="text-[10.5px]/4 font-semibold uppercase tracking-[0.11em] text-brand-blue-gray-mid sm:text-xs">{{ labels.practiceAreas }}</p>
                <p class="mt-1.5 text-[13.5px]/[1.5] font-light text-neutral-600 sm:text-base sm:leading-relaxed">{{ attorney.practiceAreasLine }}</p>
            </div>

            <div v-if="attorney.regionsServedLine" class="col-span-2">
                <p class="text-[10.5px]/4 font-semibold uppercase tracking-[0.11em] text-brand-blue-gray-mid sm:text-xs">{{ labels.regionsServed }}</p>
                <p :id="regionsServedId" class="mt-1.5 text-[13.5px]/[1.5] font-light text-neutral-600 sm:text-base sm:leading-relaxed">{{ regionsServedLine }}</p>

                <button v-if="hidesRegions"
                        type="button"
                        class="mt-3 flex w-full cursor-pointer items-center justify-end gap-[9px] whitespace-nowrap border-0 bg-transparent p-0 text-brand-teal-light transition-colors duration-[180ms] ease-[cubic-bezier(0.22,1,0.36,1)] hover:text-brand-teal focus-visible:text-brand-teal focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-lime"
                        :aria-expanded="showsAllRegions"
                        :aria-controls="regionsServedId"
                        @click="showsAllRegions = ! showsAllRegions">
                    <svg class="size-3 shrink-0 fill-current" viewBox="0 0 640 640" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path :d="regionsToggleIcon"></path></svg>
                    <span class="font-sans text-xs uppercase tracking-[0.12em]">{{ showsAllRegions ? labels.showFewerRegions : labels.viewAllRegions }}</span>
                </button>
            </div>

            <div class="col-span-2 flex sm:mt-auto">
                <a class="inline-flex cursor-pointer items-center justify-between rounded-full focus:outline-none focus:ring-0 transition gap-4 py-4 px-6 text-lg font-medium whitespace-nowrap border bg-brand-lime text-brand-teal border-transparent hover:bg-brand-lime hover:ring-1 hover:ring-offset-1 ring-offset-transparent ring-brand-lime w-full !whitespace-normal !gap-3 !px-5 !py-3 !text-sm sm:w-auto sm:!gap-4 sm:!px-6 sm:!py-4 sm:!text-lg"
                   href="#form"
                   @click.prevent="$emit('connect', attorney)">
                    <span>{{ labels.connect }}</span>
                    <svg class="h-2 w-4 fill-current rotate-90" viewBox="0 0 9 8" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M0.260254 3.28369H6.96973L4.11963 0.395508H5.71924L8.79785 3.48682V4.24219L5.71924 7.32715H4.11963L6.96973 4.42627H0.260254V3.28369Z"></path></svg>
                </a>
            </div>
        </div>
    </article>
</template>

<script setup>
import { computed, ref } from 'vue';

const props = defineProps({
    attorney: {
        type: Object,
        required: true,
    },
    labels: {
        type: Object,
        required: true,
    },
});

defineEmits(['connect']);

const avatarFailed = ref(false);

const showsAvatar = computed(() => Boolean(props.attorney.avatarUrl) && ! avatarFailed.value);

const showsAllRegions = ref(false);

/**
 * The preview line only differs from the full one when there are counties left out of it,
 * so the toggle appears exactly when it has something to reveal.
 */
const hidesRegions = computed(() => props.attorney.regionsServedPreviewLine !== props.attorney.regionsServedLine);

const regionsServedLine = computed(() => (showsAllRegions.value
    ? props.attorney.regionsServedLine
    : props.attorney.regionsServedPreviewLine));

const regionsServedId = computed(() => `directory-regions-served-${props.attorney.uuid}`);

/**
 * The plus and minus the toggle draws, kept here so the icon stays one element rather
 * than two that differ only by their path.
 */
const REGIONS_TOGGLE_ICONS = {
    expand: 'M352 128C352 110.3 337.7 96 320 96C302.3 96 288 110.3 288 128L288 288L128 288C110.3 288 96 302.3 96 320C96 337.7 110.3 352 128 352L288 352L288 512C288 529.7 302.3 544 320 544C337.7 544 352 529.7 352 512L352 352L512 352C529.7 352 544 337.7 544 320C544 302.3 529.7 288 512 288L352 288L352 128z',
    collapse: 'M96 320C96 302.3 110.3 288 128 288L512 288C529.7 288 544 302.3 544 320C544 337.7 529.7 352 512 352L128 352C110.3 352 96 337.7 96 320z',
};

const regionsToggleIcon = computed(() => (showsAllRegions.value
    ? REGIONS_TOGGLE_ICONS.collapse
    : REGIONS_TOGGLE_ICONS.expand));
</script>
